import type { SimCore, RacerInput, SimEvent } from '@/sim/SimCore';
import type { SimRacer } from '@/sim/SimRacer';
import { captureSnapshot } from '@/net/snapshot';
import { clamp } from '@/math/angles';
import { tuning } from '@/config/tuning';
import type { Snapshot } from '@/net/protocol';

/**
 * AuthoritativeRoom.ts — the SERVER core: it owns the one true {@link SimCore}
 * and is the only place a race is actually decided. Clients send inputs; this
 * steps the sim and broadcasts snapshots. It is three.js-free and has no
 * transport of its own, so the SAME class runs inside a Cloudflare Durable
 * Object (Phase 3) and inside the in-process loopback host (Phase 2 two-tab
 * proof) — only the socket plumbing around it differs.
 *
 * M69 — SCHEDULED inputs: batch inputs are queued by the client tick they are
 * FOR and consumed when the server reaches that tick, so the server applies the
 * exact input sequence the client predicted with (the client clock-syncs itself
 * to run ~RTT ahead — see clocksync.ts). A client that can't stay ahead (huge
 * RTT, no clock sync) degrades to the old M52 arrival semantics: the newest
 * received input is applied immediately and held. Held inputs no longer
 * extrapolate forever, either: after ~0.5 s of link silence the held control
 * RAMPS to a coast (a stalled player's car no longer races ahead at full
 * throttle only to yank them back on reconnect — the "reverse doesn't work"
 * bug), and after ~5 s the lane is dropped entirely so the AI takes the wheel
 * (the disconnect seam this class always promised but never wired).
 *
 * The one-shot `useItem` edge stays STICKY and loss-proof (M32/M52) but now
 * rides the queue: a scheduled edge fires AT the tick the client predicted it
 * (so range-gated items see the same world the client saw), while edges on
 * jumped-over or late ticks OR into the sticky pending edge at consumption and
 * fire on that step — never lost, never doubled (re-sends dedupe by tick).
 */

// The stall-feel knobs (coast after / ramp / AI-takeover) live in tuning.net —
// they are what every racer SEES a lagging player's car do.
/** Reject input ticks further ahead than any honest client can be: the clock
 *  lead clamps at 60 (clocksync.ts) and a batch spans 48 ticks — anything past
 *  that is a broken/hostile clock, and the bound also caps the per-step queue
 *  sweep + per-lane memory a bad client could otherwise inflate. */
const MAX_QUEUE_AHEAD = 128;

/** How far below the newest received tick a batch's NEWEST tick must sit to
 *  count as a candidate for a rewound client timeline (see REWIND_CONFIRM). */
const REWIND_GATE = 8;

/**
 * A REWOUND TIMELINE IS ADOPTED ONLY ON SUSTAINED EVIDENCE (M143).
 *
 * M105 adopted a rewind the instant ONE ordered (WebSocket) batch arrived
 * with its newest tick >8 below the newest tick ever received. That inference
 * is wrong whenever a player has two channels: the WebSocket is TCP, so any
 * lost segment or slow path makes its batches trail the WebTransport lane's
 * by far more than 8 ticks — and every such batch was then misread as "this
 * client's clock jumped backwards". The server threw away the inputs it had
 * queued, rolled `ack` back ~70 ticks, re-accepted ticks it had ALREADY
 * consumed (so one item press fired up to five times) and reported margins
 * that sent the client's clock into a resync storm — the 2026-10-02
 * production signature (44–55 resyncs and 25-unit corrections per race).
 *
 * A real rewind looks different from a trailing channel in one robust way:
 * after it, NOTHING the client sends on ANY channel advances the timeline.
 * So the room adopts a lower timeline only when batches keep arriving for
 * REWIND_CONFIRM_TICKS, at least REWIND_CONFIRM_BATCHES of them sit below the
 * gate, and not one tick was accepted in between. A WebSocket trailing a
 * LIVE datagram lane never qualifies (the lane keeps accepting); a single
 * stale straggler after a radio blackout never qualifies (the next live batch
 * resets the streak). Clients from M143 on never rewind their labels at all
 * (the protocol gate keeps older ones out), so this is purely a recovery net
 * for a wedged lane — hence the long, conservative confirmation window.
 *
 * ONE CASE STILL LOOKS LIKE A REWIND AND IS NOT (M143c): the datagram lane
 * DIES while the WebSocket trails it by more than the confirmation window.
 * Then only the trailing channel is left, every batch sits below the mark,
 * and nothing can tell that apart from a rewound clock. So the adoption is
 * built to be HARMLESS when it is wrong: it never lets a press fire twice
 * (Lane.edgeFloor), and the only other effect is that the room resumes on the
 * trailing channel a moment earlier than it otherwise would. `rewinds` in the
 * race summary counts how often it happens.
 */
const REWIND_CONFIRM_TICKS = 60; // half a second with zero forward progress
const REWIND_CONFIRM_BATCHES = 6;
/** How many recent ticks a lane remembers having received, one by one (see
 *  Lane.got). Must be a power of two. */
const GOT_RING = 256;
/** Width of one rolling window of the clock-sync signal (see Lane.newestSeen). */
const SEEN_WINDOW_TICKS = 12;

/** M105: how much sim backlog the host re-simulates after a pause before it
 *  SKIPS time instead (fastForward). 1 s ≈ 120 steps ≈ a few ms of CPU;
 *  re-simulating much more burns budget replaying a past nobody saw. */
export const CATCHUP_MAX_S = 1.0;

/** Pure accumulator policy for the host tick loop (M105): given the total
 *  backlog, how many ticks to SKIP outright (the world pauses — fastForward)
 *  so at most CATCHUP_MAX_S remains to re-simulate. The pre-M105 loop instead
 *  DELETED everything past 250 ms — serverTick fell permanently behind wall
 *  clock, every client's inputs then read "too far ahead" forever
 *  (MAX_QUEUE_AHEAD), and the race locked into the teleport-then-freeze
 *  deadlock the owner reported. */
export function catchUpPlan(accSeconds: number, dt: number): { skipTicks: number; accAfter: number } {
  if (accSeconds <= CATCHUP_MAX_S) return { skipTicks: 0, accAfter: accSeconds };
  const skipTicks = Math.ceil((accSeconds - CATCHUP_MAX_S) / dt);
  return { skipTicks, accAfter: accSeconds - skipTicks * dt };
}

/** Which transport delivered an input batch (M143) — both feed the same
 *  pipeline; the room only keeps per-channel health counters apart. */
export type InputChannel = 'ws' | 'wt';

/** Per-lane health counters (M143), cumulative for the race. They answer, from
 *  the SERVER's side, the questions every "it lagged" report raises: did the
 *  inputs arrive, on which channel, how late, and did the room ever have to
 *  hold a stale control. Read by RoomCore's race summary + the diag report. */
export interface LaneStats {
  /** Input batches received per channel. */
  batchesWs: number;
  batchesWt: number;
  /** Fresh ticks each channel delivered FIRST (the other copy was the twin). */
  newTicksWs: number;
  newTicksWt: number;
  /** Steps on which this lane's scheduled input was not there (held instead). */
  lateTicks: number;
  /** Steps this lane was live for (the denominator for lateTicks). */
  steps: number;
  /** Ticks rejected as too far ahead of the server. */
  rejectedAhead: number;
  /** WebSocket batches that trailed the newest received tick by more than the
   *  rewind gate — each one WAS a false rewind before M143. */
  wsTrailing: number;
  /** Worst such trail, in ticks. */
  wsTrailMax: number;
  /** Rewound timelines actually adopted (sustained evidence). */
  rewinds: number;
  /** Use-item edges applied. */
  edges: number;
}

const zeroStats = (): LaneStats => ({
  batchesWs: 0,
  batchesWt: 0,
  newTicksWs: 0,
  newTicksWt: 0,
  lateTicks: 0,
  steps: 0,
  rejectedAhead: 0,
  wsTrailing: 0,
  wsTrailMax: 0,
  rewinds: 0,
  edges: 0,
});

/** One connected human's input pipeline (M69). */
interface Lane {
  /** Future inputs keyed by the client tick they are FOR (scheduled). */
  queue: Map<number, RacerInput>;
  /** Newest client tick ever ACCEPTED — dedupes the ring's redundant re-sends. */
  newestReceived: number;
  /** Newest client tick SEEN lately, accepted or not (M143) — the clock-sync
   *  signal the margin is computed from. Two rules make it honest:
   *   - it counts REJECTED ticks too, so a client so far ahead that its
   *     inputs are refused reads as AHEAD (and slows down), not as silent;
   *   - it is the newest tick of the last ~100–200 ms of batches (two
   *     rolling windows), not an all-time maximum, so after a client really
   *     does move its clock back the signal follows within a couple of
   *     snapshots instead of reporting the abandoned timeline. A channel
   *     that merely trails never lowers it — the fresher channel is always
   *     inside the window. During silence it holds its last value, so the
   *     margin keeps falling exactly as it should. */
  newestSeen: number;
  seenWin: number;
  seenPrev: number;
  seenWinStart: number;
  /** The least `newestSeen − serverTick` at any step since the last broadcast
   *  snapshot (M143) — what the snapshot reports as this lane's margin. A
   *  point sample at snapshot time misses the dip between two batches; the
   *  client's input buffer must cover the WORST moment, so that is what it
   *  is told. +Infinity = no step since the last broadcast. */
  minMargin: number;
  /** Last APPLIED control, held between arrivals (useItem always false here). */
  held: RacerInput;
  /** Server tick when input last ARRIVED — the staleness clock. */
  lastArrivalTick: number;
  /** Sticky one-shot fire edge, consumed by the next step. */
  pendingEdge: boolean;
  /** Which exact ticks were received lately (M143): got[t & 255] === t. A
   *  single high-water mark is not enough with two channels — when the
   *  datagram lane LOSES a stretch (a radio blackout) and then jumps ahead,
   *  the reliable WebSocket delivers the missing ticks afterwards, below the
   *  mark. Dropping those as "re-sends" silently lost an item press made
   *  during the blackout. With the ring, a tick below the mark that was never
   *  actually received is still taken: scheduled if its turn has not come,
   *  otherwise only its one-shot press is kept. */
  got: Int32Array;
  /** Ticks at or below this were consumed before the lane was (re)created —
   *  never accepted again (a lane re-created after a silent drop must not
   *  re-apply history, nor re-fire a press from it). */
  floor: number;
  /** Ticks at or below this may still deliver CONTROLS but never a use-item
   *  press (M143c). Raised to the old high-water mark whenever a lower
   *  timeline is adopted: everything up to there was already received once,
   *  so any press among it has already had its one chance to fire. */
  edgeFloor: number;
  /** M143 rewind evidence: the current streak of below-the-gate batches that
   *  arrived with nothing accepted in between (0 = no streak). */
  belowBatches: number;
  /** Server tick when that streak began. */
  belowSince: number;
  stats: LaneStats;
}

export class AuthoritativeRoom {
  serverTick = 0;

  private readonly lanes = new Map<number, Lane>();
  /** Per-racer: the client tick of the input most recently applied (the
   *  reconcile anchor). Outlives its lane so a dropped player's last ack keeps
   *  bounding the client replay window. */
  private readonly ack: Record<number, number> = {};
  /** Per-racer health counters (M143) — kept across lane drops/rejoins. */
  private readonly stats = new Map<number, LaneStats>();

  constructor(private readonly sim: SimCore) {}

  /** This racer's input-pipeline health counters, or null if it never sent. */
  laneStats(racerId: number): LaneStats | null {
    return this.stats.get(racerId) ?? null;
  }

  /** A seat changed hands or its player reconnected (M143): the next batches
   *  belong to a NEW client clock. Forget the old timeline explicitly — never
   *  leave it to inference — but keep the held control, so the car carries on
   *  (and coasts via the usual staleness rule) until the new inputs land. */
  resetLane(racerId: number): void {
    const l = this.lanes.get(racerId);
    // Everything before now is settled: the new client's replay must not
    // reach back across the old timeline's ack.
    this.ack[racerId] = Math.max(0, this.serverTick - 1);
    if (!l) return;
    l.queue.clear();
    l.newestReceived = -1;
    l.newestSeen = -1;
    l.seenWin = -Infinity;
    l.seenPrev = -Infinity;
    l.seenWinStart = this.serverTick;
    l.minMargin = Infinity;
    l.pendingEdge = false;
    l.got.fill(-1);
    l.floor = -1; // a NEW clock: its labels owe nothing to the old numbers
    l.edgeFloor = -1;
    l.belowBatches = 0;
    // `lastArrivalTick` is deliberately left alone: if the link had been
    // silent long enough for the car to start coasting, it keeps coasting
    // until the new connection's first input lands — refreshing it here
    // handed the car its old throttle and steer back at full strength.
  }

  /** The field, by slot (racers[i].id === i) — lets the host end the match once
   *  the human slots are all finished. */
  get racers(): SimRacer[] {
    return this.sim.racers;
  }

  /** Final/live standings, ranked by BALL progress — the host reads this when a
   *  race ends to record the official per-race places for an online tour. */
  standings(): SimRacer[] {
    return this.sim.standings();
  }

  /** Assign a slot's vehicle (M51 — the room is the vehicle authority: humans'
   *  join-URL picks + randomized AI rides). Snapshots carry it to every client. */
  setVehicle(slot: number, vehicleId: string): void {
    this.sim.setVehicle(slot, vehicleId);
  }

  private lane(racerId: number): Lane {
    let l = this.lanes.get(racerId);
    if (!l) {
      l = {
        queue: new Map(),
        newestReceived: -1,
        newestSeen: -1,
        seenWin: -Infinity,
        seenPrev: -Infinity,
        seenWinStart: this.serverTick,
        minMargin: Infinity,
        held: { throttle: 0, steer: 0, useItem: false },
        lastArrivalTick: this.serverTick,
        pendingEdge: false,
        got: new Int32Array(GOT_RING).fill(-1),
        floor: this.ack[racerId] ?? -1,
        edgeFloor: -1,
        belowBatches: 0,
        belowSince: 0,
        // Stats outlive a dropped/re-created lane (a rejoin continues the race).
        stats: this.stats.get(racerId) ?? zeroStats(),
      };
      this.stats.set(racerId, l.stats);
      this.lanes.set(racerId, l);
    }
    return l;
  }

  /** LEGACY single-input path (the loopback host + the JSON control channel):
   *  apply immediately and hold — newest ARRIVAL wins, exactly the pre-M69
   *  semantics (the loopback determinism tests are pinned to it). Stored as a
   *  COPY — the caller's object may live in a client's replay history. */
  applyInput(racerId: number, input: RacerInput, inputTick: number): void {
    const l = this.lane(racerId);
    l.pendingEdge = l.pendingEdge || input.useItem === true;
    // Never let a non-finite control into the sim (M143): it becomes a NaN
    // position two ticks later and the track projection throws.
    l.held.throttle = Number.isFinite(input.throttle) ? clamp(input.throttle, -1, 1) : 0;
    l.held.steer = Number.isFinite(input.steer) ? clamp(input.steer, -1, 1) : 0;
    l.newestReceived = Math.max(l.newestReceived, inputTick);
    this.see(l, inputTick);
    l.lastArrivalTick = this.serverTick;
    this.ack[racerId] = inputTick;
  }

  /** A redundant input batch (M52): consecutive ticks, oldest first. Ticks we
   *  already hold are skipped (the ring re-sends everything ~12×); new ticks are
   *  QUEUED to apply when the server reaches them (M69). `useItem` edges ride
   *  the queue too, so an item fires at the tick the client PREDICTED it — not
   *  ~a margin early at receipt, which would evaluate range-gated items (kick/
   *  magnet) against a different world than the client saw. Edges of skipped
   *  or jumped ticks are OR-ed in at consumption (loss-proof M32, still). */
  applyInputBatch(
    racerId: number,
    entries: { tick: number; input: RacerInput }[],
    channel: InputChannel = 'ws',
  ): void {
    const l = this.lane(racerId);
    // Link liveness ≠ tick validity (M105): ANY arriving batch proves the link
    // is alive, so it ALWAYS feeds the staleness clock. Before this, a client
    // whose ticks were all rejected (e.g. "too far ahead" after the server
    // itself lost wall-clock time) read as SILENT on a perfectly healthy link:
    // the coast faded its car to a stop on every screen and the drop timer
    // handed a live player to the AI — half of the teleport-then-freeze
    // deadlock the owner reported.
    l.lastArrivalTick = this.serverTick;
    if (channel === 'wt') l.stats.batchesWt++;
    else l.stats.batchesWs++;
    if (entries.length === 0) return;
    let newest = -Infinity;
    let oldest = Infinity;
    for (const e of entries) {
      if (e.tick > newest) newest = e.tick;
      if (e.tick < oldest) oldest = e.tick;
    }
    this.see(l, newest);

    const below = l.newestReceived >= 0 && newest < l.newestReceived - REWIND_GATE;
    if (below && channel === 'ws') {
      // The WebSocket trailing the datagram lane — the exact condition that
      // used to be misread as a rewind. Counted, so production can SEE how
      // often (and how far) the reliable line falls behind.
      l.stats.wsTrailing++;
      const trail = l.newestReceived - newest;
      if (trail > l.stats.wsTrailMax) l.stats.wsTrailMax = trail;
    }

    const accepted = this.accept(l, entries, channel);
    if (accepted > 0) {
      l.belowBatches = 0; // forward progress on some channel — no rewind
    } else if (below) {
      if (l.belowBatches === 0) l.belowSince = this.serverTick;
      l.belowBatches++;
      if (
        l.belowBatches >= REWIND_CONFIRM_BATCHES &&
        this.serverTick - l.belowSince >= REWIND_CONFIRM_TICKS
      ) {
        // Sustained evidence: every channel has gone quiet on the old
        // timeline and keeps sending this lower one. Adopt it. Edges still
        // waiting in the queue are kept (loss-proof M32); the queued future
        // ticks themselves belong to the abandoned timeline.
        for (const q of l.queue.values()) l.pendingEdge = l.pendingEdge || q.useItem;
        l.queue.clear();
        // NO PRESS AT OR BELOW THE OLD HIGH-WATER MARK FIRES AGAIN (M143c).
        // Every label up to there was already received once — its press, if
        // it had one, was applied or has just been kept above. The first cut
        // only stripped presses from THIS batch; the batches after it then
        // re-delivered the same labels into a ring that had just been wiped,
        // and a press from the last moments before the adoption fired twice.
        // That is not a corner case: a datagram lane that dies while the
        // WebSocket trails more than half a second looks exactly like a
        // rewind (an independent review found it). The price is a press
        // genuinely made on re-used labels by a client that really did
        // rewind — and no client since M143 rewinds at all.
        l.edgeFloor = Math.max(l.edgeFloor, l.newestReceived);
        l.newestReceived = oldest - 1;
        l.got.fill(-1); // the ring described the abandoned timeline
        l.floor = -1;
        l.belowBatches = 0;
        l.stats.rewinds++;
        // Clamp at 0: ack rides the wire as a u32 (codec), so a -1 would
        // decode as 4294967295 and the client's replay filter would discard
        // its ENTIRE input history on the next reconcile.
        this.ack[racerId] = Math.max(0, Math.min(this.ack[racerId] ?? 0, oldest - 1));
        this.accept(l, entries, channel);
      }
    }
  }

  /** Feed the clock-sync signal with a batch's newest tick (see Lane.newestSeen). */
  private see(l: Lane, newest: number): void {
    const age = this.serverTick - l.seenWinStart;
    if (age >= SEEN_WINDOW_TICKS) {
      l.seenPrev = age >= 2 * SEEN_WINDOW_TICKS ? -Infinity : l.seenWin;
      l.seenWin = -Infinity;
      l.seenWinStart = this.serverTick;
    }
    if (newest > l.seenWin) l.seenWin = newest;
    l.newestSeen = Math.max(l.seenWin, l.seenPrev);
  }

  /** Take every entry the lane has not received yet; returns how many were
   *  new. Dedupe is PER TICK (see Lane.got), not by a single high-water mark. */
  private accept(
    l: Lane,
    entries: { tick: number; input: RacerInput }[],
    channel: InputChannel,
  ): number {
    let accepted = 0;
    for (const e of entries) {
      const t = e.tick;
      if (t <= l.floor) continue; // settled before this lane existed
      if (t > this.serverTick + MAX_QUEUE_AHEAD) {
        l.stats.rejectedAhead++; // broken clock
        continue;
      }
      const slot = t & (GOT_RING - 1);
      if (l.got[slot] === t) continue; // redundant re-send (the ring sends each tick ~12×)
      // A press only counts above the edge floor (see Lane.edgeFloor).
      const press = e.input.useItem && t > l.edgeFloor;
      if (t <= l.newestReceived) {
        // Below the mark but never received: the other channel skipped it.
        if (t <= l.newestReceived - GOT_RING) continue; // too old to tell apart — drop
        l.got[slot] = t;
        if (t >= this.serverTick) {
          // Its turn has not come yet — schedule it like any other.
          l.queue.set(t, {
            throttle: e.input.throttle,
            steer: e.input.steer,
            useItem: press,
          });
          accepted++;
        } else if (press) {
          // Its turn has passed (the room held an older control for that
          // tick, and that is history) — but a press is never lost (M32).
          l.pendingEdge = true;
        }
        continue;
      }
      l.got[slot] = t;
      l.queue.set(t, {
        throttle: e.input.throttle,
        steer: e.input.steer,
        useItem: press,
      });
      l.newestReceived = t;
      accepted++;
    }
    if (channel === 'wt') l.stats.newTicksWt += accepted;
    else l.stats.newTicksWs += accepted;
    return accepted;
  }

  /** M105: the transport host discovered it was paused for longer than its
   *  catch-up budget — SKIP the tick counter over the lost wall-clock time.
   *  Physics does not run for the skipped ticks (the outage froze the world
   *  for everyone equally); what matters is that `serverTick` lands back on
   *  wall clock, because every client keeps counting in real time and the
   *  input-scheduling window (MAX_QUEUE_AHEAD) is anchored to `serverTick` —
   *  before this, one >1 s pause left every future input batch "too far
   *  ahead" FOREVER: the self-locking freeze. Jumped-over queue entries obey
   *  the usual rules: the newest becomes the held control, fire edges OR in
   *  (loss-proof M32), and the outage never reads as link silence. */
  fastForward(ticks: number): void {
    if (ticks <= 0) return;
    this.serverTick += ticks;
    for (const [id, l] of this.lanes) {
      let newestPast = -1;
      for (const [t, q] of l.queue) {
        if (t >= this.serverTick) continue;
        l.pendingEdge = l.pendingEdge || q.useItem;
        if (t > newestPast) newestPast = t;
      }
      if (newestPast >= 0) {
        const late = l.queue.get(newestPast) as RacerInput;
        l.held.throttle = late.throttle;
        l.held.steer = late.steer;
        this.ack[id] = Math.max(this.ack[id] ?? -1, newestPast);
      }
      for (const t of [...l.queue.keys()]) if (t < this.serverTick) l.queue.delete(t);
      l.lastArrivalTick = this.serverTick; // the outage was OURS, not the link's
    }
  }

  /** A human dropped — hand their slot to the AI (the seam from SimCore). */
  dropRacer(racerId: number): void {
    this.lanes.delete(racerId);
  }

  /** Pick this step's control for one lane. Exact scheduled tick if we have it;
   *  a LATE client (not running ahead) falls back to newest-arrival semantics;
   *  otherwise hold the last applied control. Consumed entries' `useItem` edges
   *  (including jumped-over ticks') OR into the sticky pendingEdge. */
  private consume(racerId: number, l: Lane): void {
    const s = this.serverTick;
    if (l.queue.has(s)) {
      const input = l.queue.get(s) as RacerInput;
      l.held.throttle = input.throttle;
      l.held.steer = input.steer;
      this.ack[racerId] = s;
      for (const [t, q] of l.queue) {
        if (t > s) continue;
        l.pendingEdge = l.pendingEdge || q.useItem;
        l.queue.delete(t);
      }
      return;
    }
    // Late fallback (M52 semantics): everything we hold is already in the past —
    // apply the newest and hold it. (A gap BELOW queued future ticks — the ring
    // restarting after a forward clock resync — just holds until those arrive.)
    if (l.newestReceived < s && l.newestReceived > (this.ack[racerId] ?? -1)) {
      const late = l.queue.get(l.newestReceived);
      if (late) {
        l.held.throttle = late.throttle;
        l.held.steer = late.steer;
        this.ack[racerId] = l.newestReceived;
        for (const q of l.queue.values()) l.pendingEdge = l.pendingEdge || q.useItem;
        l.queue.clear();
      }
    }
  }

  /** Link-silence coast (M69): full control for 0.5 s, then ramp to zero. */
  private staleFactor(l: Lane): number {
    const stale = this.serverTick - l.lastArrivalTick;
    if (stale <= tuning.net.staleCoastAfterTicks) return 1;
    return Math.max(0, 1 - (stale - tuning.net.staleCoastAfterTicks) / tuning.net.staleCoastRampTicks);
  }

  /** Advance the authoritative sim one fixed step; returns this step's events. */
  step(dt: number): SimEvent[] {
    // Fresh input objects per step (a handful of tiny allocations at 120 Hz —
    // nothing): reusing them would let a retained reference silently mutate
    // next tick, the aliasing bug class this file has always defended against.
    const inputs = new Map<number, RacerInput>();
    for (const [id, l] of this.lanes) {
      if (this.serverTick - l.lastArrivalTick > tuning.net.staleDropAfterTicks) {
        this.lanes.delete(id); // silent drop → AI takeover (rejoin re-creates it)
        continue;
      }
      l.stats.steps++;
      if (!l.queue.has(this.serverTick)) l.stats.lateTicks++;
      // Only once an input has been SEEN on this clock (M143c). A lane just
      // reset for a rejoin has none yet (newestSeen = -1), and folding
      // "-1 − serverTick" in here made the first margin the new connection
      // was told about roughly minus the whole race so far — one phantom
      // clock jump on every rejoin, the very moment that must be clean.
      if (l.newestSeen >= 0) {
        const m = l.newestSeen - this.serverTick;
        if (m < l.minMargin) l.minMargin = m;
      }
      this.consume(id, l);
      if (l.pendingEdge) l.stats.edges++;
      const f = this.staleFactor(l);
      inputs.set(id, {
        throttle: l.held.throttle * f,
        steer: l.held.steer * f,
        useItem: l.pendingEdge,
      });
    }
    this.sim.step(inputs, dt);
    this.serverTick++;
    // Consume the fire edge so a held input can't keep firing the item.
    for (const l of this.lanes.values()) l.pendingEdge = false;
    return this.sim.events.slice();
  }

  /** Capture the current authoritative state. `margins` tells each client how
   *  far ahead of the server its inputs are arriving (in ticks) — the feedback
   *  signal for the client-side clock sync (M69).
   *
   *  `forBroadcast` (M143, what RoomCore passes): each margin is the MINIMUM
   *  over the steps since the previous broadcast, and the interval restarts.
   *  Without it (tests, tools) the call is a pure point sample with no side
   *  effect. */
  snapshot(forBroadcast = false): Snapshot {
    const margins: Record<number, number> = {};
    // From the newest tick SEEN (M143), so a client whose inputs are being
    // rejected as too far ahead reads as ahead — and slows down — instead of
    // reading as silent and jumping further forward.
    for (const [id, l] of this.lanes) {
      // No input seen yet on this clock (a lane just reset for a rejoin):
      // there is nothing to report — a made-up "infinitely late" reading
      // would send the fresh client's controller chasing a phantom.
      if (l.newestSeen < 0) continue;
      const point = l.newestSeen - this.serverTick;
      const m = forBroadcast ? Math.min(point, l.minMargin) : point;
      if (forBroadcast) l.minMargin = Infinity;
      margins[id] = clamp(m, -32767, 32767);
    }
    return captureSnapshot(this.sim, this.serverTick, this.ack, margins);
  }
}
